import { useMutation } from '@apollo/client';
import { InsertThirdPartyContactApiDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalForm } from 'src/components/form/form/ModalForm';

import { evictField } from '@/utils/graphqlUtils';

import { AddContactForm } from './AddContactForm';
import type { AddContactSchemaFields } from './schema';
import { addContactDefaultValues, createAddContactSchema } from './schema';

interface Props {
  thirdPartyId: string;
  existingEmails: string[];
  isVisible: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
}

export const AddContactModal: FC<Props> = ({
  thirdPartyId,
  existingEmails,
  isVisible,
  onDismiss,
  onSuccess,
}) => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'third_party.contacts',
  });

  const schema = useMemo(
    () => createAddContactSchema(existingEmails),
    [existingEmails]
  );

  const [mutate] = useMutation(InsertThirdPartyContactApiDocument, {
    update: (cache) => {
      evictField(cache, 'third_party_contact');
    },
  });

  const onSave = async (values: AddContactSchemaFields) => {
    await mutate({
      variables: {
        ...values,
        ThirdPartyId: thirdPartyId,
      },
    });
    onSuccess();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <ModalForm<AddContactSchemaFields>
      defaultValues={addContactDefaultValues}
      i18n={{ entity_name: t('entity_name') }}
      schema={schema}
      onSave={onSave}
      onDismiss={() => onDismiss()}
      formId={'add-contact-form'}
      visible={isVisible}
      readOnly={false}
    >
      <AddContactForm />
    </ModalForm>
  );
};
